import React, { useEffect } from 'react'
import { Prompt } from 'react-router-dom'

export const UNSAVED_CHANGES_MESSAGE =
  'Are you sure you want to leave? Changes you made may not be saved.'

interface UnsavedChangesGuardProps {
  /** Whether the surface has unsaved changes. When false the guard is inert. */
  when: boolean
  /** Confirmation copy. Defaults to the app-wide survey-editor message. */
  message?: string
  /**
   * Also warn on hard refresh / tab-close via `beforeunload` (default `true`).
   * Set `false` for surfaces that only guard in-app navigation — e.g. the
   * payment modals, which additionally gate their own dismiss via
   * `window.confirm` and were never specced to warn on refresh.
   */
  guardRefresh?: boolean
}

/**
 * Shared unsaved-changes guard. Renders a React Router v5 `<Prompt>` to
 * intercept in-app navigation and, when `guardRefresh` is set, installs a
 * `beforeunload` listener for hard refresh / tab-close.
 *
 * Renders as a normal element, so it works in both function components
 * (`return <UnsavedChangesGuard ... />`) and class components (render it in
 * the tree). This is the canonical replacement for the copy-pasted
 * `<Prompt>` + `window.onbeforeunload` pattern.
 *
 * The refresh guard uses `addEventListener`/`removeEventListener` with its own
 * handler rather than assigning `window.onbeforeunload`, so multiple guards on
 * the same page compose instead of clobbering each other's singleton slot.
 */
export default function UnsavedChangesGuard({
  when,
  message = UNSAVED_CHANGES_MESSAGE,
  guardRefresh = true,
}: UnsavedChangesGuardProps): React.ReactElement {
  useEffect(() => {
    if (!when || !guardRefresh) {
      return undefined
    }
    function handleBeforeUnload(event: BeforeUnloadEvent) {
      // The browser shows its native "Leave site?" dialog when the event is
      // cancelled. `returnValue` is the legacy signal some Chromium versions
      // still require.
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [when, guardRefresh])

  return <Prompt when={when} message={message} />
}



// WEBPACK FOOTER //
// ./src/shared/components/UnsavedChangesGuard/index.tsx